/**
 * Post-authentication return paths.
 *
 * Anything that bounces an unauthenticated request to `/signin` appends where
 * the user was headed so they land back there after signing in. Two spellings
 * are in the wild — `redirectUrl` (SessionProvider, api-client,
 * AgentAccessRequired) and `returnUrl` (custom-domain redirects) — so readers
 * accept both rather than silently dropping one caller's return path.
 *
 * Only same-origin, path-shaped values are honoured. An attacker-supplied
 * absolute URL in that parameter would otherwise turn every sign-in link into
 * an open redirect.
 *
 * @module utils/return-url
 */

/** Query parameters that may carry a post-auth destination, in priority order. */
const RETURN_PARAMS = ["redirectUrl", "returnUrl"] as const

/**
 * Reads the return path from a query string, if present and safe.
 *
 * @param params - The current URL's search parameters
 * @returns A relative path (always starting with a single `/`), or null
 */
export function getReturnUrl(params: URLSearchParams): string | null {
  for (const key of RETURN_PARAMS) {
    const raw = params.get(key)
    if (!raw) continue
    const safe = sanitizeReturnUrl(raw)
    if (safe) return safe
  }
  return null
}

/**
 * Normalizes a candidate return value to a safe in-app path.
 *
 * Rejects absolute URLs, protocol-relative `//host` paths, and anything that
 * isn't rooted at `/` — all of which could send the user off-origin.
 *
 * @param raw - Raw (possibly percent-encoded) parameter value
 * @returns The relative path, or null when it isn't safe to navigate to
 */
export function sanitizeReturnUrl(raw: string): string | null {
  let value = raw
  try {
    value = decodeURIComponent(raw)
  } catch {
    // Malformed percent-encoding — fall through with the raw value; the
    // shape checks below still decide whether it's usable.
  }

  value = value.trim()
  if (!value.startsWith("/")) return null
  // `//evil.com` and `/\evil.com` are protocol-relative in browsers.
  if (value.startsWith("//") || value.startsWith("/\\")) return null
  return value
}

/**
 * Appends the current return path to a sign-in / sign-up link so the
 * destination survives a hop through another auth screen.
 *
 * @param path - Target route (e.g. "/signup")
 * @param params - The current URL's search parameters
 * @returns `path`, with `?redirectUrl=…` appended when there is one to carry
 */
export function withReturnUrl(path: string, params: URLSearchParams): string {
  const returnUrl = getReturnUrl(params)
  if (!returnUrl) return path
  const separator = path.includes("?") ? "&" : "?"
  return `${path}${separator}redirectUrl=${encodeURIComponent(returnUrl)}`
}
